import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { fetchPreview } from '../services/mapManagementService';
import './MapImportPreview.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const STYLE = 'mapbox://styles/mapbox/dark-v11';
const EMPTY = { type: 'FeatureCollection', features: [] };

// Difficulty palette for the incoming annotation. Current geometry is drawn in a
// single muted grey instead — the operator is comparing shapes, and two colored
// layers on top of each other is unreadable.
const RUN_COLORS = [
  ['green', '#22c55e', 'Green'],
  ['blue', '#3b82f6', 'Blue'],
  ['red', '#ef4444', 'Red'],
  ['black', '#e2e8f0', 'Black'],
  ['double_black', '#a855f7', 'Double black'],
  ['grey', '#eab308', 'Terrain park'],
  ['orange', '#f97316', 'Freeride'],
  ['yellow', '#facc15', 'Extreme'],
];
const RUN_COLOR_FALLBACK = '#94a3b8';
const LIFT_COLOR = '#fb923c';
const POI_COLOR = '#fbbf24';
const CURRENT_COLOR = '#64748b';
const BOUNDARY_NEW = '#38bdf8';

const RUN_COLOR_EXPR = [
  'match', ['get', 'colorName'],
  ...RUN_COLORS.flatMap(([value, hex]) => [value, hex]),
  RUN_COLOR_FALLBACK,
];

const asCollection = (geometry) => (geometry
  ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry, properties: {} }] }
  : EMPTY);

// layer id -> which toggle controls it
const GROUPS = {
  'mip-cur-runs-line': 'current',
  'mip-cur-lifts-line': 'current',
  'mip-cur-boundary-fill': 'currentBoundary',
  'mip-cur-boundary-line': 'currentBoundary',
  'mip-new-runs-line': 'incoming',
  'mip-new-lifts-line': 'incoming',
  'mip-new-pois-pt': 'incoming',
  'mip-new-boundary-fill': 'incomingBoundary',
  'mip-new-boundary-line': 'incomingBoundary',
};

const HOVER_LAYERS = [
  'mip-new-runs-line', 'mip-new-lifts-line', 'mip-new-pois-pt',
  'mip-cur-runs-line', 'mip-cur-lifts-line',
];

const MapImportPreview = ({ runId }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [visible, setVisible] = useState({
    current: true, currentBoundary: true, incoming: true, incomingBoundary: true,
  });

  useEffect(() => {
    let cancelled = false;
    fetchPreview(runId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [runId]);

  const addLayers = useCallback((map, d) => {
    const sources = {
      'mip-cur-boundary': asCollection(d.current.boundary),
      'mip-cur-lifts': d.current.lifts,
      'mip-cur-runs': d.current.runs,
      'mip-new-boundary': asCollection(d.incoming?.boundary),
      'mip-new-lifts': d.incoming?.lifts || EMPTY,
      'mip-new-runs': d.incoming?.runs || EMPTY,
      'mip-new-pois': d.incoming?.pois || EMPTY,
    };
    for (const [id, geojson] of Object.entries(sources)) {
      if (!map.getSource(id)) map.addSource(id, { type: 'geojson', data: geojson });
      else map.getSource(id).setData(geojson);
    }

    // Current beneath incoming, boundaries beneath lines, so the annotation reads
    // on top of what it replaces.
    const layers = [
      { id: 'mip-cur-boundary-fill', type: 'fill', source: 'mip-cur-boundary',
        paint: { 'fill-color': CURRENT_COLOR, 'fill-opacity': 0.07 } },
      { id: 'mip-cur-boundary-line', type: 'line', source: 'mip-cur-boundary',
        paint: { 'line-color': CURRENT_COLOR, 'line-width': 1.5, 'line-dasharray': [3, 2] } },
      { id: 'mip-new-boundary-fill', type: 'fill', source: 'mip-new-boundary',
        paint: { 'fill-color': BOUNDARY_NEW, 'fill-opacity': 0.06 } },
      { id: 'mip-new-boundary-line', type: 'line', source: 'mip-new-boundary',
        paint: { 'line-color': BOUNDARY_NEW, 'line-width': 2 } },

      { id: 'mip-cur-lifts-line', type: 'line', source: 'mip-cur-lifts',
        paint: { 'line-color': CURRENT_COLOR, 'line-width': 4, 'line-opacity': 0.55 } },
      { id: 'mip-cur-runs-line', type: 'line', source: 'mip-cur-runs',
        paint: { 'line-color': CURRENT_COLOR, 'line-width': 4, 'line-opacity': 0.55 } },

      { id: 'mip-new-lifts-line', type: 'line', source: 'mip-new-lifts',
        paint: { 'line-color': LIFT_COLOR, 'line-width': 3 },
        layout: { 'line-cap': 'round' } },
      { id: 'mip-new-runs-line', type: 'line', source: 'mip-new-runs',
        paint: { 'line-color': RUN_COLOR_EXPR, 'line-width': 3 },
        layout: { 'line-cap': 'round' } },
      { id: 'mip-new-pois-pt', type: 'circle', source: 'mip-new-pois',
        paint: {
          'circle-radius': 6, 'circle-color': POI_COLOR,
          'circle-stroke-color': '#0f172a', 'circle-stroke-width': 2,
        } },
    ];

    for (const layer of layers) {
      if (!map.getLayer(layer.id)) map.addLayer(layer);
    }

    const coords = [];
    const collect = (fc) => (fc?.features || []).forEach(f => {
      const g = f.geometry;
      if (!g) return;
      if (g.type === 'LineString') coords.push(...g.coordinates);
      else if (g.type === 'Point') coords.push(g.coordinates);
      else if (g.type === 'Polygon') g.coordinates.forEach(r => coords.push(...r));
      else if (g.type === 'MultiPolygon') g.coordinates.forEach(p => p.forEach(r => coords.push(...r)));
    });
    collect(d.incoming?.runs);
    collect(d.incoming?.lifts);
    if (coords.length === 0) { collect(d.current.runs); collect(d.current.lifts); }
    if (coords.length > 0) {
      const bounds = coords.reduce(
        (acc, c) => acc.extend(c),
        new mapboxgl.LngLatBounds(coords[0], coords[0]),
      );
      map.fitBounds(bounds, { padding: 48, duration: 0 });
    }
  }, []);

  // Hover: name plus what the feature is, so an operator can identify a line
  // without cross-referencing the table below.
  const attachHover = useCallback((map) => {
    popupRef.current = new mapboxgl.Popup({
      closeButton: false, closeOnClick: false, offset: 12, className: 'mip-popup',
    });

    const describe = (feature) => {
      const p = feature.properties || {};
      const incoming = feature.layer.id.startsWith('mip-new');
      const label = RUN_COLORS.find(([v]) => v === p.colorName);
      const kind = p.colorName ? (label ? label[2] : p.colorName)
        : p.liftType ? String(p.liftType).replace(/_/g, ' ')
          : p.type ? String(p.type)
            : 'feature';
      return `<strong>${p.name || 'Unnamed'}</strong>`
        + `<span class="mip-popup-kind">${kind}</span>`
        + `<span class="mip-popup-origin">${incoming ? 'incoming' : 'current'}`
        + `${p.id ? ` · ${p.id}` : ''}</span>`;
    };

    const onMove = (e) => {
      const feature = e.features && e.features[0];
      if (!feature) return;
      map.getCanvas().style.cursor = 'pointer';
      popupRef.current.setLngLat(e.lngLat).setHTML(describe(feature)).addTo(map);
    };
    const onLeave = () => {
      map.getCanvas().style.cursor = '';
      popupRef.current.remove();
    };

    for (const id of HOVER_LAYERS) {
      if (!map.getLayer(id)) continue;
      map.on('mousemove', id, onMove);
      map.on('mouseleave', id, onLeave);
    }
  }, []);

  useEffect(() => {
    if (!data || !containerRef.current || mapRef.current) return;
    if (!MAPBOX_TOKEN) { setError('VITE_MAPBOX_TOKEN is not set.'); return; }

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLE,
      center: [data.current.centre?.lng ?? 0, data.current.centre?.lat ?? 0],
      zoom: 12,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapRef.current = map;
    map.on('load', () => { addLayers(map, data); attachHover(map); });

    return () => {
      if (popupRef.current) popupRef.current.remove();
      map.remove();
      mapRef.current = null;
    };
  }, [data, addLayers, attachHover]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    for (const [layerId, group] of Object.entries(GROUPS)) {
      if (!map.getLayer(layerId)) continue;
      map.setLayoutProperty(layerId, 'visibility', visible[group] ? 'visible' : 'none');
    }
  }, [visible, data]);

  const toggle = (key) => setVisible(v => ({ ...v, [key]: !v[key] }));

  if (error) return <div className="mip-error">Map preview unavailable: {error}</div>;
  if (!data) return <div className="mip-loading">Loading map…</div>;

  const hasIncoming = Boolean(data.incoming);
  const noBoundary = hasIncoming && !data.incoming.boundary;

  return (
    <div className="mip">
      <div className="mip-toolbar">
        <label>
          <input type="checkbox" checked={visible.incoming} onChange={() => toggle('incoming')} />
          Incoming geometry
        </label>
        <label>
          <input type="checkbox" checked={visible.incomingBoundary}
            onChange={() => toggle('incomingBoundary')} disabled={noBoundary} />
          Incoming boundary
        </label>
        <span className="mip-divider" />
        <label>
          <input type="checkbox" checked={visible.current} onChange={() => toggle('current')} />
          Current geometry
        </label>
        <label>
          <input type="checkbox" checked={visible.currentBoundary}
            onChange={() => toggle('currentBoundary')} />
          Current boundary
        </label>
      </div>

      <div ref={containerRef} className="mip-canvas" />

      <div className="mip-legend">
        <div className="mip-legend-group">
          <span className="mip-legend-title">Incoming runs</span>
          {RUN_COLORS.map(([value, hex, label]) => (
            <span className="mip-legend-item" key={value}>
              <span className="mip-line" style={{ background: hex }} />{label}
            </span>
          ))}
        </div>
        <div className="mip-legend-group">
          <span className="mip-legend-title">Also</span>
          <span className="mip-legend-item">
            <span className="mip-line" style={{ background: LIFT_COLOR }} />Lift
          </span>
          <span className="mip-legend-item">
            <span className="mip-dot" style={{ background: POI_COLOR }} />POI
          </span>
          <span className="mip-legend-item">
            <span className="mip-line mip-line-thick" style={{ background: CURRENT_COLOR }} />
            Current runs and lifts
          </span>
          <span className="mip-legend-item">
            <span className="mip-line mip-line-dashed" />Current boundary
          </span>
          <span className="mip-legend-item">
            <span className="mip-line" style={{ background: BOUNDARY_NEW }} />Incoming boundary
          </span>
        </div>
      </div>

      {noBoundary && (
        <p className="mip-warn">
          The new boundary could not be derived, so only today&apos;s is drawn.
        </p>
      )}
    </div>
  );
};

export default MapImportPreview;
